import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Loader2, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ToastAction } from "@/components/ui/toast";
import { toast } from "@/hooks/use-toast";
import { TemplatesNavIcon } from "@/components/icons/NavIcons";
import { useAuth } from "@/context/AuthContext";
import { addTemplateSection, getAllTemplates, trackTemplateUsage } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Templates on the prescription pad. A template belongs to one section (complaints, diagnosis,
 * examination, medicines, tests, advice, quick notes) and is reached from that section's own field: type its
 * name in the box, or open the small list behind the template button. Applying one only adds —
 * nothing already written is replaced — and the toast offers Undo.
 */

export type RxTemplateType = "complaint" | "medicine" | "investigation" | "advice" | "diagnosis" | "quick_notes" | "examination";

export interface RxTemplate {
  id: string;
  title: string;
  type: RxTemplateType;
  value: any;
  category?: string;
  usage_count?: number;
}

interface RxTemplatesValue {
  /** Every template the doctor can use, most used first */
  templates: RxTemplate[];
  apply: (template: RxTemplate) => void;
  save: (type: RxTemplateType, title: string, shareWithClinic: boolean) => Promise<boolean>;
  /** The section has something worth saving */
  hasContent: (type: RxTemplateType) => boolean;
  suggestedTitle: (type: RxTemplateType) => string;
  /** Ask a section's template list to open on its "save" form (the tip under Medicines) */
  requestSave: (type: RxTemplateType) => void;
  saveRequest: { type: RxTemplateType; n: number } | null;
}

export const RxTemplatesContext = createContext<RxTemplatesValue | null>(null);

const FIELD: Record<RxTemplateType, string> = {
  complaint: "complaints",
  medicine: "medicines",
  investigation: "investigations",
  advice: "advice",
  diagnosis: "diagnosis",
  quick_notes: "quick_notes",
  examination: "examination",
};

const LABEL: Record<RxTemplateType, string> = {
  complaint: "complaints",
  medicine: "medicines",
  investigation: "tests",
  advice: "advice",
  diagnosis: "diagnosis",
  quick_notes: "notes",
  examination: "examination",
};

const TEXT_TYPES = new Set<RxTemplateType>(["advice", "diagnosis", "quick_notes", "examination"]);

// The API may return the value as a JSON string
function parseValue(raw: any): any {
  if (typeof raw === "string") {
    try { return JSON.parse(raw); } catch { return raw; }
  }
  return raw;
}

const textOf = (v: any): string => (typeof v?.value === "string" ? v.value : typeof v === "string" ? v : "").trim();
const medicineName = (m: any): string => String(m?.brand_name || m?.product || "").trim();
const testName = (i: any): string => String(typeof i === "string" ? i : i?.name || "").trim();
const tagsOf = (text: string): string[] => text.split(",").map(s => s.trim()).filter(Boolean);

/** One line saying what a template holds: "Dolo 650 Tablet, Lo-Platt 75 Tablet" */
export function getPreview(template: Pick<RxTemplate, "type" | "value">): string {
  if (!template.value) return "";
  const v = parseValue(template.value);
  if (template.type === "complaint" && Array.isArray(v))
    return v.map((c: any) => (typeof c === "string" ? c : c?.complaint)).filter(Boolean).join(", ");
  if (template.type === "medicine" && Array.isArray(v))
    return v.map(medicineName).filter(Boolean).join(", ");
  if (template.type === "investigation" && Array.isArray(v))
    return v.map(testName).filter(Boolean).join(", ");
  const text = textOf(v);
  return text.length > 120 ? text.slice(0, 120) + "…" : text;
}

/** Owns the pad's templates: loads them once, applies one to the form, saves a section as a new one. */
export function useRxTemplatesProvider(form: UseFormReturn<any>): RxTemplatesValue {
  const { currentUser } = useAuth();
  const doctorId = currentUser?.doctor_id || "";
  const clinicId = currentUser?.clinic_id || "";
  const [templates, setTemplates] = useState<RxTemplate[]>([]);
  const [saveRequest, setSaveRequest] = useState<RxTemplatesValue["saveRequest"]>(null);
  const { getValues, setValue } = form;

  const load = useCallback(async () => {
    if (!doctorId) return;
    try {
      const data: any = await getAllTemplates(doctorId, clinicId, { limit: 200 });
      const list: RxTemplate[] = data?.templates ?? (Array.isArray(data) ? data : []);
      setTemplates([...list].sort((a, b) => (b.usage_count ?? 0) - (a.usage_count ?? 0)));
    } catch { /* the pad works without templates */ }
  }, [doctorId, clinicId]);
  useEffect(() => { load(); }, [load]);

  // What the section holds now, without its empty rows; null when there is nothing
  const contentOf = useCallback((type: RxTemplateType): any => {
    const current = getValues(FIELD[type]);
    if (TEXT_TYPES.has(type)) return String(current ?? "").trim() || null;
    const rows = (Array.isArray(current) ? current : []).filter((row: any) =>
      type === "complaint" ? String(row?.complaint ?? "").trim() : type === "medicine" ? medicineName(row) : testName(row));
    return rows.length ? rows : null;
  }, [getValues]);

  const apply = useCallback((template: RxTemplate) => {
    const field = FIELD[template.type];
    const before = structuredClone(getValues(field));
    const raw = parseValue(template.value);
    const existing = contentOf(template.type);
    let next: any;
    let added = 0;

    switch (template.type) {
      case "complaint": {
        const have = new Set((existing ?? []).map((c: any) => String(c.complaint).trim().toLowerCase()));
        const incoming = (Array.isArray(raw) ? raw : [])
          .map((item: any) => typeof item === "string"
            ? { complaint: item, frequency: "", severity: "", duration: "", date: "" }
            : { complaint: item?.complaint || "", frequency: item?.frequency || "", severity: item?.severity || "", duration: item?.duration || "", date: item?.date || "" })
          .filter((c: any) => c.complaint.trim() && !have.has(c.complaint.trim().toLowerCase()));
        next = [...(existing ?? []), ...incoming];
        added = incoming.length;
        break;
      }
      case "medicine": {
        const have = new Set((existing ?? []).map((m: any) => medicineName(m).toLowerCase()));
        const incoming = (Array.isArray(raw) ? raw : [])
          .filter((m: any) => medicineName(m) && !have.has(medicineName(m).toLowerCase()))
          .map((m: any) => ({
            drug_id:      null,
            product:      medicineName(m),
            brand_name:   medicineName(m),
            generic_name: m.generic_name || "",
            dosage:       m.dosage       ?? "",
            taken_when:   m.taken_when   ?? m.when ?? "",
            duration:     m.duration     ?? "",
            quantity:     m.quantity     ?? "",
            notes:        m.notes        ?? "",
            _drug_form:   "",
            _drug_brand:  "",
          }));
        next = [...(existing ?? []), ...incoming];
        added = incoming.length;
        break;
      }
      case "investigation": {
        const have = new Set((existing ?? []).map((i: any) => testName(i).toLowerCase()));
        const incoming = (Array.isArray(raw) ? raw : []).filter((i: any) => testName(i) && !have.has(testName(i).toLowerCase()));
        next = [...(existing ?? []), ...incoming];
        added = incoming.length;
        break;
      }
      case "diagnosis": {
        const current = tagsOf(existing ?? "");
        const have = new Set(current.map(t => t.toLowerCase()));
        const incoming = tagsOf(textOf(raw)).filter(t => !have.has(t.toLowerCase()));
        next = [...current, ...incoming].join(", ");
        added = incoming.length;
        break;
      }
      default: {
        const text = textOf(raw);
        const current: string = existing ?? "";
        if (text && !current.toLowerCase().includes(text.toLowerCase())) {
          next = current ? `${current}\n${text}` : text;
          added = 1;
        }
      }
    }

    if (!added) {
      toast({ title: `“${template.title}” is already in this prescription` });
      return;
    }
    const dirty = { shouldDirty: true, shouldTouch: true };
    setValue(field, next, dirty);
    toast({
      title: `Added “${template.title}”`,
      description: getPreview(template),
      action: <ToastAction altText="Undo" onClick={() => setValue(field, before, dirty)}>Undo</ToastAction>,
    });
    trackTemplateUsage(template.id, doctorId).catch(() => {});
  }, [getValues, setValue, contentOf, doctorId]);

  const save = useCallback(async (type: RxTemplateType, title: string, shareWithClinic: boolean) => {
    const content = contentOf(type);
    if (!content || !title.trim()) return false;
    try {
      await addTemplateSection({
        type,
        title: title.trim(),
        category: shareWithClinic ? "clinic" : "personal",
        doctor_id: doctorId,
        clinic_id: clinicId,
        value: TEXT_TYPES.has(type) ? { value: content } : content,
      });
      toast({ title: "Template saved", description: title.trim() });
      await load();
      return true;
    } catch {
      toast({ title: "Couldn't save the template", description: "Please try again.", variant: "destructive" });
      return false;
    }
  }, [contentOf, doctorId, clinicId, load]);

  // Templates are mostly named after the condition, so the diagnosis is the best first guess
  const suggestedTitle = useCallback((type: RxTemplateType) => {
    const diagnosis = tagsOf(String(getValues("diagnosis") ?? ""))[0] ?? "";
    if (diagnosis) return diagnosis.slice(0, 40);
    const content = contentOf(type);
    if (type === "complaint") return content?.[0]?.complaint ?? "";
    if (type === "medicine") return content ? medicineName(content[0]) : "";
    if (type === "investigation") return content ? testName(content[0]) : "";
    return "";
  }, [getValues, contentOf]);

  const hasContent = useCallback((type: RxTemplateType) => !!contentOf(type), [contentOf]);
  const requestSave = useCallback((type: RxTemplateType) => setSaveRequest(r => ({ type, n: (r?.n ?? 0) + 1 })), []);

  return useMemo(
    () => ({ templates, apply, save, hasContent, suggestedTitle, requestSave, saveRequest }),
    [templates, apply, save, hasContent, suggestedTitle, requestSave, saveRequest],
  );
}

/** Templates of one section whose name contains what is being typed in that section's box */
export function useTemplateMatches(type: RxTemplateType, text: string): RxTemplate[] {
  const ctx = useContext(RxTemplatesContext);
  const typed = text.trim().toLowerCase();
  return useMemo(
    () => (ctx && typed.length >= 2
      ? ctx.templates.filter(t => t.type === type && t.title.toLowerCase().includes(typed)).slice(0, 3)
      : []),
    [ctx, type, typed],
  );
}

export function useApplyTemplate() {
  return useContext(RxTemplatesContext)?.apply;
}

/** A template row at the end of a section's suggestion list */
export function TemplateOption({ template, active, onPick }: { template: RxTemplate; active: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onPick(); }}
      className={cn(
        "w-full text-left px-3 py-2 text-sm border-b border-border/40 last:border-0 flex items-center gap-2 min-w-0",
        active ? "bg-primary/10 text-primary" : "hover:bg-accent text-foreground",
      )}
    >
      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">Template</span>
      <span className="shrink-0 font-medium">{template.title}</span>
      <span className="truncate text-xs text-muted-foreground">{getPreview(template)}</span>
    </button>
  );
}

/**
 * The template button of one section: a small list of that section's templates (most used first),
 * and — when the section has something in it — "Save current as template" with the name typed there.
 * Renders nothing outside the prescription pad.
 */
export function TemplateMenu({ type, withLabel = false, className }: { type: RxTemplateType; withLabel?: boolean; className?: string }) {
  const ctx = useContext(RxTemplatesContext);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [share, setShare] = useState(false);
  const [busy, setBusy] = useState(false);

  const startSave = () => {
    setTitle(ctx?.suggestedTitle(type) ?? "");
    setShare(false);
    setSaving(true);
  };

  const request = ctx?.saveRequest;
  useEffect(() => {
    if (request?.type !== type) return;
    setOpen(true);
    startSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  if (!ctx) return null;

  const all = ctx.templates.filter(t => t.type === type);
  const q = query.trim().toLowerCase();
  const shown = q ? all.filter(t => t.title.toLowerCase().includes(q) || getPreview(t).toLowerCase().includes(q)) : all;
  const canSave = open && ctx.hasContent(type);

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) { setQuery(""); setSaving(false); }
  };

  const submit = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    const ok = await ctx.save(type, title, share);
    setBusy(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {withLabel ? (
          <Button type="button" variant="outline" size="sm" aria-label={`${LABEL[type]} templates`} className={cn("h-8 touch:h-10 shrink-0 gap-1.5 px-2.5 sm:px-3 text-[13px] font-medium", className)} onClick={(e) => e.stopPropagation()}>
            {/* Phones: the icon alone, so the add box keeps its width */}
            <TemplatesNavIcon className="w-4 h-4" /> <span className="hidden sm:inline">Templates</span>
          </Button>
        ) : (
          <button
            type="button"
            title="Templates"
            aria-label={`${LABEL[type]} templates`}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "touch-target shrink-0 flex items-center justify-center w-7 h-7 rounded text-muted-foreground/70 hover:text-foreground hover:bg-accent data-[state=open]:bg-accent data-[state=open]:text-foreground",
              className,
            )}
          >
            <TemplatesNavIcon className="w-4 h-4" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={16}
        className="w-80 max-w-[calc(100vw-2rem)] p-0"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {!saving && all.length > 6 && (
          <div className="p-2 border-b border-divider">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${LABEL[type]} templates…`}
              className="h-8 touch:h-10 text-sm"
            />
          </div>
        )}
        {!saving && (
          <div className="max-h-64 overflow-y-auto py-1">
            {shown.length > 0 ? shown.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => { ctx.apply(t); onOpenChange(false); }}
                className="w-full text-left px-3 py-1.5 touch:py-2.5 outline-none hover:bg-accent focus-visible:bg-accent"
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="truncate text-sm font-medium text-foreground">{t.title}</span>
                  {t.category === "clinic" && <span className="shrink-0 text-2xs text-muted-foreground">Clinic</span>}
                </span>
                <span className="block truncate text-xs text-muted-foreground">{getPreview(t)}</span>
              </button>
            )) : (
              <p className="px-3 py-3 text-[13px] text-muted-foreground">
                {all.length ? "No template matches." : `No ${LABEL[type]} templates yet.`}
              </p>
            )}
          </div>
        )}
        <div className={cn("p-2", !saving && "border-t border-divider")}>
          {saving ? (
            <div className="space-y-2.5 p-1">
              <p className="text-[13px] font-semibold text-foreground">Save current {LABEL[type]} as a template</p>
              <Input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
                placeholder="Template name, e.g. Viral fever"
                maxLength={80}
                className="h-9 touch:h-10 text-sm"
              />
              <label className="flex items-center gap-2 text-[13px] text-foreground cursor-pointer">
                <Checkbox checked={share} onCheckedChange={(c) => setShare(c === true)} />
                Share with clinic
              </label>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setSaving(false)}>Cancel</Button>
                <Button type="button" size="sm" onClick={submit} disabled={!title.trim() || busy} className="gap-1.5">
                  {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
                </Button>
              </div>
            </div>
          ) : canSave ? (
            <button
              type="button"
              onClick={startSave}
              className="w-full flex items-center gap-1.5 rounded px-2 py-1.5 touch:py-2.5 text-[13px] font-medium text-primary hover:bg-accent"
            >
              <Plus className="w-3.5 h-3.5" /> Save current {LABEL[type]} as template
            </button>
          ) : (
            <p className="px-2 py-1 text-xs text-muted-foreground">Fill this section to save it as a template.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
